// Site appearance: the look-and-feel options an agency can configure for its
// public website. It lives inside the storefront document so appearance changes
// flow through the same draft -> publish -> revert workflow as the site copy.
//
// Every option is applied through CSS custom properties on the public site
// root, so the ported site components stay untouched: Tailwind v4 utilities
// already resolve `var(--font-sans)` and `var(--radius-*)`.

import { z } from 'zod';

import { darkenHex } from '@/lib/color';
import {
  DESIGN_IDS,
  SITE_DESIGNS,
  defaultSections,
  designById,
  mergeSections,
  type DesignId,
} from '@/lib/storefront/designs';

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** A colour the agency picked, or `''` when the site should keep the brand. */
const colorOrInherit = z.union([hexColor, z.literal('')]);

export type FontId = 'system' | 'inter' | 'poppins' | 'manrope';
export type CornerStyle = 'sharp' | 'rounded' | 'soft';
export type HeroLayout = 'centered' | 'image-left' | 'full-bleed';

export interface FontChoice {
  id: FontId;
  label: string;
  /** Font stack applied through Tailwind's `--font-sans`. */
  stack: string;
  /** Google Fonts stylesheet, omitted for the system stack. */
  href?: string;
}

export const FONT_CHOICES: FontChoice[] = [
  {
    id: 'system',
    label: 'Système',
    stack:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
  },
  {
    id: 'inter',
    label: 'Inter',
    stack: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap',
  },
  {
    id: 'poppins',
    label: 'Poppins',
    stack: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap',
  },
  {
    id: 'manrope',
    label: 'Manrope',
    stack: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    href: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap',
  },
];

export interface CornerChoice {
  id: CornerStyle;
  label: string;
  /** Overrides the Tailwind radius scale on the public site root. */
  radii: { md: string; lg: string; xl: string; '2xl': string; '3xl': string };
}

export const CORNER_CHOICES: CornerChoice[] = [
  {
    id: 'sharp',
    label: 'Angles nets',
    radii: { md: '0px', lg: '0px', xl: '0px', '2xl': '0px', '3xl': '0px' },
  },
  {
    id: 'rounded',
    label: 'Arrondis',
    radii: { md: '0.375rem', lg: '0.5rem', xl: '0.75rem', '2xl': '1rem', '3xl': '1.5rem' },
  },
  {
    id: 'soft',
    label: 'Très arrondis',
    radii: { md: '0.75rem', lg: '1rem', xl: '1.25rem', '2xl': '1.75rem', '3xl': '2.5rem' },
  },
];

export interface HeroLayoutChoice {
  id: HeroLayout;
  label: string;
  description: string;
}

export const HERO_LAYOUTS: HeroLayoutChoice[] = [
  { id: 'full-bleed', label: 'Photo plein écran', description: 'Grande photo, texte superposé.' },
  { id: 'centered', label: 'Centré', description: 'Texte centré sur la photo.' },
  { id: 'image-left', label: 'Photo à gauche', description: 'Photo à gauche, texte à droite.' },
];

export interface PalettePreset {
  id: string;
  label: string;
  primaryColor: string;
  secondaryColor: string;
}

export const PALETTE_PRESETS: PalettePreset[] = [
  { id: 'sunset', label: 'Sunset', primaryColor: '#FF8A00', secondaryColor: '#0d1117' },
  { id: 'ocean', label: 'Ocean', primaryColor: '#0EA5E9', secondaryColor: '#0F172A' },
  { id: 'emerald', label: 'Emerald', primaryColor: '#10B981', secondaryColor: '#064E3B' },
  { id: 'royal', label: 'Royal', primaryColor: '#6366F1', secondaryColor: '#1E1B4B' },
  { id: 'crimson', label: 'Crimson', primaryColor: '#E11D48', secondaryColor: '#1F2937' },
  { id: 'sand', label: 'Sand', primaryColor: '#D97706', secondaryColor: '#292524' },
];

export interface SectionToggles {
  [sectionId: string]: boolean;
}

export interface SiteAppearance {
  /** Whole-page template: chrome + home page composition. */
  design: DesignId;
  /** `#RRGGBB`, or `''` to keep the agency profile's brand colour. */
  primaryColor: string;
  secondaryColor: string;
  /** Wordmark text; empty keeps the agency name. */
  logoText: string;
  /** Logo image path or URL; empty keeps the wordmark. */
  logoUrl: string;
  font: FontId;
  corners: CornerStyle;
  heroLayout: HeroLayout;
  /** Visibility of the sections the chosen design declares. */
  sections: SectionToggles;
}

/** Used when neither the appearance nor the agency profile sets a colour. */
export const FALLBACK_PRIMARY_COLOR = '#FF8A00';
export const FALLBACK_SECONDARY_COLOR = '#0d1117';

export const DEFAULT_APPEARANCE: SiteAppearance = {
  design: SITE_DESIGNS[0].id,
  primaryColor: '',
  secondaryColor: '',
  logoText: '',
  logoUrl: '',
  font: 'system',
  corners: 'rounded',
  heroLayout: 'full-bleed',
  sections: defaultSections(SITE_DESIGNS[0]),
};

/** A field that falls back to `value` instead of failing the whole parse. */
const fallback = <T extends z.ZodType>(schema: T, value: z.infer<T>) => schema.catch(value);

const sectionsSchema = fallback(z.record(z.string(), z.boolean()), {});

export const appearanceSchema = z
  .object({
    design: fallback(z.enum(DESIGN_IDS), DEFAULT_APPEARANCE.design),
    primaryColor: fallback(colorOrInherit, DEFAULT_APPEARANCE.primaryColor),
    secondaryColor: fallback(colorOrInherit, DEFAULT_APPEARANCE.secondaryColor),
    logoText: fallback(z.string().max(40), DEFAULT_APPEARANCE.logoText),
    logoUrl: fallback(z.string().max(2000), DEFAULT_APPEARANCE.logoUrl),
    font: fallback(z.enum(['system', 'inter', 'poppins', 'manrope']), DEFAULT_APPEARANCE.font),
    corners: fallback(z.enum(['sharp', 'rounded', 'soft']), DEFAULT_APPEARANCE.corners),
    heroLayout: fallback(z.enum(['full-bleed', 'centered', 'image-left']), DEFAULT_APPEARANCE.heroLayout),
    sections: sectionsSchema,
  })
  // The design owns the section list: fill in the switches it declares and drop
  // keys left over from another design.
  .transform(
    (value): SiteAppearance => ({
      ...value,
      sections: mergeSections(designById(value.design), value.sections),
    }),
  );

/** Never throws: unknown or partial input falls back to the defaults. */
export function parseAppearance(value: unknown): SiteAppearance {
  const parsed = appearanceSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_APPEARANCE;
}

export function fontChoice(font: FontId): FontChoice {
  return FONT_CHOICES.find((choice) => choice.id === font) ?? FONT_CHOICES[0];
}

export function cornerChoice(corners: CornerStyle): CornerChoice {
  return CORNER_CHOICES.find((choice) => choice.id === corners) ?? CORNER_CHOICES[1];
}

export interface BrandColors {
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * The colours the site actually paints: what the agency picked in the
 * appearance editor, otherwise the brand colour from the agency profile.
 */
export function resolvedColors(
  appearance: SiteAppearance,
  brand?: BrandColors,
): { primaryColor: string; secondaryColor: string } {
  return {
    primaryColor: appearance.primaryColor || brand?.primaryColor || FALLBACK_PRIMARY_COLOR,
    secondaryColor: appearance.secondaryColor || brand?.secondaryColor || FALLBACK_SECONDARY_COLOR,
  };
}

/**
 * The custom properties that carry an appearance onto the public site root.
 * Because the values are set on one element, they never leak into the admin
 * console which shares the same document.
 *
 * `brand` supplies the colours still in force while the agency has not picked
 * any, so a branded site is not reset to the defaults by this feature. The dark
 * shade is only overridden when the appearance owns the colour, which keeps the
 * stylesheet's own hover shade for the inherited brand.
 */
export function appearanceCssVars(
  appearance: SiteAppearance,
  brand?: BrandColors,
): Record<string, string> {
  const font = fontChoice(appearance.font);
  const corners = cornerChoice(appearance.corners);
  const { primaryColor, secondaryColor } = resolvedColors(appearance, brand);

  const vars: Record<string, string> = {
    '--color-primary': primaryColor,
    '--color-secondary': secondaryColor,
    '--font-sans': font.stack,
    '--radius-md': corners.radii.md,
    '--radius-lg': corners.radii.lg,
    '--radius-xl': corners.radii.xl,
    '--radius-2xl': corners.radii['2xl'],
    '--radius-3xl': corners.radii['3xl'],
  };

  if (appearance.primaryColor) {
    vars['--color-primary-dark'] = darkenHex(appearance.primaryColor);
  }

  return vars;
}

/** Injects the Google Fonts stylesheet for a chosen font, once per document. */
export function ensureFontLoaded(font: FontId): void {
  if (typeof document === 'undefined') return;
  const choice = fontChoice(font);
  if (!choice.href) return;
  const id = `brand-font-${choice.id}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = choice.href;
  document.head.appendChild(link);
}
